import { useUpdateScholarshipApplicationStatus } from '@/apis/family_application/student-details.requests'
import { useAppAlert } from '@/app/AppAlertProvider'
import DocumentPreviewNew from '@/app/common/components/Documents/DocumentPreviewNew'
import {
  global_error_codes,
  STATUS_AGE_PHASE_OUT,
} from '@/app/common/constants'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { AppRoutePaths } from '@/routing/route_paths'
import { updateStudent } from '@/storage/applications/upsert-student.action'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice.ts'
import { AppDispatch, RootState } from '@/storage/store'
import { format, parseISO } from 'date-fns'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  setSelectedStudentDOB,
  updateStudentBCInformation
} from '../../../../storage/applications/familyApplicationSlice'
import { usePostBirthCertificate } from './hooks/usePostBirthCertificate'
import useStudenDetailsDocumentParams from './hooks/useStudentDetailsDocumentParams'
import { useWizardContext } from './StudentDetailsWizardProvider'
import { useTypesafeStudent } from './useTypesafeStudent'

export interface ErrorException extends Error {
  code?: number
  details?: {
    date_of_birth?: string
    errorsMessage?: string[]
  }
}

interface IAlertConfig {
  variant: 'error' | 'success' | 'warning'
  titleText?: string
  message?: string
  confirmBtnText?: string
  denyBtn?: boolean
  denyBtnText?: string
  messageComponent?: React.ReactNode
}

interface IAlertCallBack {
  denied?: boolean
  confirmed?: boolean
  canceled?: boolean
}

export const StudentPhotoReviewPage = () => {
  const ProgramId = useSelector(selectProgramID)
  const { t: translate } = useTranslation()
  const { formData } = useWizardContext()
  const navigateTo = useNavigate()
  const mutationPostBC = usePostBirthCertificate()
  const mutationUpdateScholarship = useUpdateScholarshipApplicationStatus()
  const { onStudentDetailFormSelect, selectedOption } =
    useStudenDetailsDocumentParams()
  const { fireAlert } = useAppAlert()
  const asyncDispatch = useDispatch<AppDispatch>()
  const {
    ocr_bc_attempt_number: ocrAttempt,
    selectedStudentId,
    data,
  } = useSelector((state: RootState) => state.FamilyApplication)
  const {
    ocr_bc_many_attempts,
    ocr_bc_more_than_18_years,
    ocr_bc_more_than_21_years,
    ocr_bc_multiple_errors,
  } = global_error_codes
  const { fullName, student} = useTypesafeStudent()

  const handleSubmit = () => {
    if (formData.DateOfBirthFile) {
      const form = new FormData()

      form.append('file', formData.DateOfBirthFile)
      form.append('document_type', 'BC')
      form.append('attempt_number', String(1))
      form.append('student_id', String(selectedStudentId))
      form.append('program_id', String(ProgramId))
      form.append('application_id', String(data!.application_id))

      mutationPostBC.mutate(form, {
        onSuccess: (response) => {
          asyncDispatch(updateStudentBCInformation(response))

          const isDateMismatch =
            response.date_of_birth !== student?.date_of_birth
          fireAlert({
            variant: 'success',
            titleText: translate('date_of_birth_identified'),
            messageComponent: (
              <>
                {translate('date_correct', {
                  date: format(parseISO(response.date_of_birth), 'MM-dd-y'),
                  student_name: fullName,
                })}
                {isDateMismatch && (
                  <>
                  {" "}
                  {
                    student.date_of_birth && (
                      translate("dob_will_be_updated", {
                        "date_of_birth": format(parseISO(student.date_of_birth), 'MM-dd-y')
                      }))}
                  </>
                )}
                <p className="pt-2">{translate('if_not_correct_retry')}.</p>
              </>
            ),
            denyBtn: true,
            denyBtnText: translate('retry'),
            confirmBtnText: translate('confirm'),
          }).then(({ confirmed, denied }) => {
            if (confirmed) {
              fireAlert({
                variant: 'success',
                titleText: translate('parent_of_student'),
                messageComponent: (
                  <div className="text-start text-sm">
                    <p>{translate('i_acknowledge_that')}:</p>
                    <p>{translate('only_a_parent_of_student_can_to_apply')}</p>

                    <p className="mt-3">{translate('what_does_this_means')}</p>
                    <p>
                      {translate(
                        'if_you_are_not_the_student_parent_or_adoptive'
                      )}
                    </p>
                  </div>
                ),
                denyBtn: true,
                denyBtnText: translate('retry'),
                confirmBtnText: translate('confirm'),
              }).then(({ confirmed, denied }) => {
                if (confirmed) {
                  navigateTo(
                    AppRoutePaths.family_application.student_details
                      .document_submitted
                  )
                }
                if (denied) {
                  navigateTo(
                    AppRoutePaths.family_application.student_details
                      .enter_details
                  )
                }
              })
            }
            if (denied) {
              navigateTo(
                AppRoutePaths.family_application.student_details.enter_details
              )
            }
          })
        },

        onError: ({ code, details, message }: ErrorException) => {
          const shouldDisplayTryAgain = ocrAttempt < 3

          let alertConfig: IAlertConfig = {
            variant: 'error',
            confirmBtnText: translate('submit'),
            denyBtn: shouldDisplayTryAgain,
            denyBtnText: translate('try_again'),
            titleText: translate('error_processing'),
            messageComponent: (
              <>
                {code !== ocr_bc_multiple_errors && (
                  <p className="text-sm text-center mb-2">{message}</p>
                )}

                {code !== ocr_bc_many_attempts &&
                  code !== ocr_bc_more_than_18_years &&
                  code !== ocr_bc_more_than_21_years && (
                    <p className=" text-center text-sm mt-1 text-gray-800">
                      {translate('support_review_docs')}
                    </p>
                  )}
              </>
            ),
          }
          let callBack = ({ denied, confirmed, canceled }: IAlertCallBack) => {
            if (denied) {
              navigateTo(
                AppRoutePaths.family_application.student_details.capture
              )
              return
            }
            if (confirmed || canceled) {
              asyncDispatch(
                updateStudent({
                  program_id: ProgramId,
                  student_id: selectedStudentId ?? undefined,
                  applicant_doesnt_have_birth_cert: !!selectedOption,
                })
              )

              navigateTo(
                AppRoutePaths.family_application.student_details.relationship
                  .document_submitted
              )
              return
            }
          }

          switch (code) {
            case ocr_bc_many_attempts:
              callBack = ({ confirmed, canceled }) => {
                if (confirmed || canceled) {
                  navigateTo(
                    AppRoutePaths.family_application.student_details
                      .relationship.document_submitted
                  )
                }
              }
              break
            case ocr_bc_more_than_18_years:
              alertConfig = {
                ...alertConfig,
                titleText: translate('the_student_is'),
                variant: 'warning',
                denyBtn: true,
                denyBtnText: 'No',
                confirmBtnText: translate('yes'),
              }
              callBack = ({ confirmed, denied, canceled }) => {
                if (confirmed) {
                  asyncDispatch(
                    setSelectedStudentDOB(String(details?.date_of_birth))
                  )
                  onStudentDetailFormSelect('form_other')
                }
                if (denied || canceled) {
                  mutationUpdateScholarship.mutate({
                    student_id: Number(selectedStudentId),
                    status: STATUS_AGE_PHASE_OUT,
                  })
                  fireAlert({
                    variant: 'error',
                    titleText: translate('student_is_too_old'),
                  }).then(() => {
                    navigateTo(AppRoutePaths.home.index)
                  })
                }
              }
              break
            case ocr_bc_more_than_21_years:
              alertConfig = {
                ...alertConfig,
                variant: 'warning',
                titleText: '',
                denyBtn: true,
                denyBtnText: translate('cancel'),
                confirmBtnText: translate('retry'),
              }
              callBack = ({ confirmed, denied }) => {
                if (confirmed) {
                  navigateTo(-1)
                }
                if (denied) {
                  navigateTo(AppRoutePaths.home.index)
                }
              }
              break
            default:
              alertConfig = {
                ...alertConfig,
              }
              break
          }

          fireAlert(alertConfig).then(callBack)
        },
      })
    } else {
      fireAlert({
        variant: 'error',
        message: translate('document_is_missing'),
      }).then(() => {
        navigateTo(-1)
      })
    }
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('photo_review')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-full">
        <DocumentPreviewNew
          status={mutationPostBC.status}
          file={formData.DateOfBirthFile}
          onContinueClick={handleSubmit}
          onRetakeClick={() => {
            navigateTo(AppRoutePaths.family_application.student_details.capture)
          }}
        />
      </FamilyWizardLayout.Content>
    </FamilyWizardLayout>
  )
}
